import type { PropsWithChildren } from 'react'
import { Suspense, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Trans } from '@lingui/react/macro'
import { ErrorBoundary } from '@sentry/react'

import { useBrandingInfo, useFeatures } from '@jotta/grpc-connect-client/config'
import { AppLayout } from '@jotta/ui/AppLayout'
import { ErrorPage } from '@jotta/ui/ErrorPage'
import { Footer } from '@jotta/ui/Footer'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { CreateUser } from '../../auth/CreateUser'
import { useFederationStore } from '../../auth/useFederationStore'

import { Header } from './Header'
import { WebappModuleNav } from './WebappModuleNav'

function SignupDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  return (
    <ModalDialog open={open} onClose={onClose}>
      <header className="h2 text-center">
        <Trans>Create a free account</Trans>
      </header>
      <CreateUser
        className="p-8"
        type="private"
        allowAppPromotion
        keepRouteOnSuccess
        federationIntent={{
          case: 'signup',
          value: {},
        }}
      />
    </ModalDialog>
  )
}

type WebappSection =
  | 'referral'
  | 'verifyEmail'
  | 'sharedphotos'
  | 'sharedfiles'
  | 'not_found'
  | 'terms'
  | 'cookies'
  | 'download'
  | 'version'
  | 'afterterminate'
  | 'signupaccepted'
  | 'upgradeaccepted'
  | 'paymentstatus'
  | 'support'

function hasSignup(section: WebappSection) {
  switch (section) {
    case 'support':
    case 'not_found':
    case 'terms':
    case 'download':
    case 'cookies':
    case 'version':
      return true
    case 'upgradeaccepted':
    case 'afterterminate':
    case 'signupaccepted':
    case 'paymentstatus':
    case 'sharedphotos':
    case 'sharedfiles':
    case 'referral':
    case 'verifyEmail':
      return false
    default:
      return exhaustiveGuard(section)
  }
}

function hasFooter(section: WebappSection) {
  switch (section) {
    case 'afterterminate':
    case 'signupaccepted':
    case 'paymentstatus':
    case 'support':
    case 'upgradeaccepted':
    case 'not_found':
    case 'terms':
    case 'download':
    case 'cookies':
    case 'version':
    case 'referral':
    case 'verifyEmail':
      return true
    case 'sharedphotos':
    case 'sharedfiles':
      return false
    default:
      return exhaustiveGuard(section)
  }
}

export const PublicLayout = observer<
  PropsWithChildren<{
    section: WebappSection
    className?: string
  }>
>(function WebappLayoutPublic({ children = <Outlet />, section, className }) {
  const { signup } = useFeatures()
  const { pendingFederation } = useFederationStore()
  const brandStore = useBrandStore()
  const { signupFreeLink } = useBrandingInfo()
  const [signingUp, setSigningUp] = useState(Boolean(pendingFederation))

  const startSignup =
    signup && hasSignup(section)
      ? () => {
          if (signupFreeLink) {
            window.location.assign(signupFreeLink)
          } else {
            setSigningUp(true)
          }
        }
      : undefined

  useEffect(() => {
    brandStore.activateElements({
      leftNav: false,
      moduleNav: false,
      timelineScrubber: false,
    })
  }, [brandStore])
  return (
    <AppLayout
      header={<Header onStartSignup={startSignup} />}
      moduleNav={<WebappModuleNav />}
      footer={hasFooter(section) ? <Footer /> : undefined}
      className={className}
    >
      <ErrorBoundary
        fallback={({ error, resetError }) => (
          <ErrorPage error={error} resetError={resetError} />
        )}
        beforeCapture={scope => {
          const translated =
            document.body.parentElement?.classList.contains('translated-ltr') ||
            false
          return scope
            .addBreadcrumb({
              level: 'info',
              category: 'PublicLayout',
              data: {
                section,
                translated,
              },
            })
            .setTag('section', section)
        }}
      >
        <Suspense fallback={<LoadingOverlay id="PublicLayout" open />}>
          <SignupDialog open={signingUp} onClose={() => setSigningUp(false)} />
          {children}
        </Suspense>
      </ErrorBoundary>
    </AppLayout>
  )
})
